import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { API_URL, authApi } from '@/lib/api';
import { Bell, Plus, Trash2, Zap, Save, Loader2, QrCode as QrIcon } from 'lucide-react';

/**
 * Exhibitor-facing announcements & coupons manager.
 * - One-click "Launch 30-min Flash Deal"
 * - Full CRUD (title/body/discount/dates)
 * - Live QR preview for each coupon
 */
export default function AnnouncementsManager({ boothNumber }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [qrCache, setQrCache] = useState({}); // { announcementId: sanitizedSvg }
  const [form, setForm] = useState({
    type: 'flash_sale',
    title: '',
    body: '',
    discount_label: 'BUY 1 FREE 1',
    discount_pct: null,
    minutes: 30,
  });

  const authHeaders = { Authorization: `Bearer ${authApi.getToken()}`, Accept: 'application/json' };

  const notify = useCallback((msg, isError = false) => {
    setFlash({ msg, isError });
    setTimeout(() => setFlash(null), 2500);
  }, []);

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/exhibitor/announcements`, { headers: authHeaders });
      setItems(res.data.data || []);
    } catch (e) {
      // Not fatal
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const launchQuick = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/exhibitor/announcements/quick-promo`, {
        minutes: Number(form.minutes) || 30,
        discount_label: form.discount_label,
      }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      notify('Flash deal live!');
      fetchList();
    } catch (e) {
      notify(e.response?.data?.message || 'Failed to launch', true);
    } finally {
      setSaving(false);
    }
  };

  const createCustom = async () => {
    if (!form.title.trim()) { notify('Title is required', true); return; }
    setSaving(true);
    try {
      const now = new Date();
      const scheduledDate = form.scheduled_at ? new Date(form.scheduled_at) : null;
      const startsAt = scheduledDate && scheduledDate > now ? scheduledDate : now;
      const expires = new Date(startsAt.getTime() + (Number(form.minutes) || 30) * 60 * 1000);
      const res = await axios.post(`${API_URL}/exhibitor/announcements`, {
        type: form.type,
        title: form.title,
        body: form.body || undefined,
        discount_label: form.discount_label || undefined,
        discount_pct: form.discount_pct ? Number(form.discount_pct) : undefined,
        starts_at: toMysql(startsAt),
        expires_at: toMysql(expires),
        send_push: !!form.send_push,
        scheduled_at: scheduledDate ? toMysql(scheduledDate) : undefined,
        is_active: true,
      }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      const pushInfo = res.data?.push;
      if (pushInfo) {
        notify(`Published + push sent to ${pushInfo.sent} device${pushInfo.sent === 1 ? '' : 's'}`);
      } else if (form.send_push && scheduledDate && scheduledDate > now) {
        notify(`Scheduled — push will fire at ${scheduledDate.toLocaleString()}`);
      } else {
        notify('Announcement published');
      }
      setForm((p) => ({ ...p, title: '', body: '', scheduled_at: '' }));
      fetchList();
    } catch (e) {
      notify(e.response?.data?.message || 'Failed', true);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await axios.patch(`${API_URL}/exhibitor/announcements/${a.id}`, { is_active: !a.is_active }, {
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
      });
      fetchList();
    } catch (e) {
      notify(e.response?.data?.message || 'Failed', true);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API_URL}/exhibitor/announcements/${id}`, { headers: authHeaders });
      setItems((prev) => prev.filter((x) => x.id !== id));
      notify('Deleted');
    } catch (e) {
      notify(e.response?.data?.message || 'Failed', true);
    }
  };

  const loadQr = async (a) => {
    if (qrCache[a.id]) return;
    try {
      const res = await axios.get(`${API_URL}/announcements/${a.id}/qr`);
      const sanitized = DOMPurify.sanitize(res.data.data.qr_svg, { USE_PROFILES: { svg: true, svgFilters: true } });
      setQrCache((prev) => ({ ...prev, [a.id]: sanitized }));
    } catch {
      // Not live (expired) — silently ignore
    }
  };

  return (
    <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-6 border border-slate-800 mt-6" data-testid="announcements-manager">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="w-4 h-4 text-yellow-400" />
        <div className="text-emerald-400 text-[10px] uppercase tracking-widest font-bold">Announcements & Coupons</div>
      </div>

      {flash && (
        <div className={`mb-3 px-3 py-2 rounded-lg text-xs font-semibold ${flash.isError ? 'bg-red-500/20 text-red-200 border border-red-500/40' : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40'}`} data-testid="ann-flash">
          {flash.msg}
        </div>
      )}

      {/* Quick promo bar */}
      <div className="p-3 rounded-xl bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-red-500/10 border border-yellow-500/30 mb-6">
        <div className="text-yellow-300 text-[10px] uppercase font-black tracking-widest">Quick flash deal</div>
        <div className="text-white text-xs mb-2">Push a limited-time offer to every visitor's phone in one click.</div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={form.discount_label}
            onChange={(e) => setForm((p) => ({ ...p, discount_label: e.target.value }))}
            className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs"
            data-testid="quick-promo-label"
          >
            <option>BUY 1 FREE 1</option>
            <option>50% OFF</option>
            <option>30% OFF</option>
            <option>FREE SAMPLE</option>
            <option>RM10 OFF</option>
          </select>
          <input
            type="number"
            min={5}
            max={240}
            value={form.minutes}
            onChange={(e) => setForm((p) => ({ ...p, minutes: e.target.value }))}
            className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs w-20"
            data-testid="quick-promo-minutes"
          />
          <span className="text-slate-400 text-xs">mins</span>
          <button
            onClick={launchQuick}
            disabled={saving}
            className="ml-auto px-3 py-1.5 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-slate-900 text-xs font-black flex items-center gap-1.5 disabled:opacity-50"
            data-testid="launch-quick-promo-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Launch Flash Deal
          </button>
        </div>
      </div>

      {/* Custom form */}
      <details className="mb-6 rounded-lg border border-slate-800 bg-slate-950/40" data-testid="custom-announcement-form">
        <summary className="cursor-pointer px-3 py-2 text-white text-xs font-bold flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Custom announcement…
        </summary>
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} className={inputCls} data-testid="ann-type">
              <option value="flash_sale">Flash Sale</option>
              <option value="offer">Offer</option>
              <option value="announcement">Announcement</option>
            </select>
            <input value={form.discount_label} onChange={(e) => setForm((p) => ({ ...p, discount_label: e.target.value }))} placeholder="Discount label (e.g. 30% OFF)" className={inputCls} data-testid="ann-discount-label" />
          </div>
          <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Title (required)" className={inputCls} data-testid="ann-title" />
          <textarea value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} rows={3} placeholder="Details (optional)" className={inputCls} data-testid="ann-body" />

          {/* Push scheduling row */}
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-slate-200 text-xs bg-slate-950/60 border border-slate-700 rounded-lg px-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.send_push}
                onChange={(e) => setForm((p) => ({ ...p, send_push: e.target.checked }))}
                className="w-4 h-4 accent-emerald-500"
                data-testid="ann-send-push"
              />
              <span className="flex-1">
                <span className="block text-white font-bold">📱 Send push notification</span>
                <span className="block text-[10px] text-slate-400">Fires on every visitor's phone lock-screen</span>
              </span>
            </label>
            <div>
              <label className="text-slate-400 text-[10px] uppercase font-bold tracking-widest block mb-1">Schedule for (optional)</label>
              <input
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setForm((p) => ({ ...p, scheduled_at: e.target.value }))}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs"
                data-testid="ann-scheduled-at"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">Leave blank to send immediately</div>
            </div>
          </div>

          <div className="flex items-end gap-2">
            <div>
              <label className="text-slate-400 text-[10px] uppercase font-bold tracking-widest block mb-1">Valid for</label>
              <input type="number" min={5} max={720} value={form.minutes} onChange={(e) => setForm((p) => ({ ...p, minutes: e.target.value }))} className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs w-24" />
            </div>
            <span className="text-slate-400 text-xs mb-2">minutes</span>
            <button
              onClick={createCustom}
              disabled={saving}
              className="ml-auto px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
              data-testid="publish-announcement-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Publish
            </button>
          </div>
        </div>
      </details>

      {/* List */}
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-xs text-center py-6">No announcements yet. Launch your first flash deal above.</div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="rounded-lg border border-slate-800 p-3 bg-slate-950/40" data-testid={`ann-row-${a.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest ${
                      a.type === 'flash_sale' ? 'bg-red-500/20 text-red-300' :
                      a.type === 'offer' ? 'bg-emerald-500/20 text-emerald-300' :
                      'bg-blue-500/20 text-blue-300'
                    }`}>{a.type.replace('_', ' ')}</span>
                    {a.discount_label && (
                      <span className="text-[10px] font-black text-slate-900 bg-yellow-400 px-2 py-0.5 rounded-full">{a.discount_label}</span>
                    )}
                    <span className={`text-[10px] font-bold ${a.is_active ? 'text-emerald-400' : 'text-slate-500'}`}>
                      {a.is_active ? '● LIVE' : '○ paused'}
                    </span>
                  </div>
                  <div className="text-white font-bold text-sm">{a.title}</div>
                  {a.body && <div className="text-slate-300 text-xs mt-1">{a.body}</div>}
                  <div className="text-slate-500 text-[10px] mt-1">
                    Coupon: <span className="text-emerald-300 font-mono font-bold">{a.coupon_code}</span>
                    {a.redemption_count > 0 && <> · <span className="text-yellow-300 font-bold">{a.redemption_count} redeemed</span></>}
                    {a.expires_at && <> · expires {new Date(a.expires_at).toLocaleString()}</>}
                    {a.send_push && a.scheduled_at && !a.push_sent && (
                      <> · <span className="text-sky-300 font-bold">🕒 push at {new Date(a.scheduled_at).toLocaleString()}</span></>
                    )}
                    {a.send_push && a.push_sent && a.push_sent_at && (
                      <> · <span className="text-emerald-300 font-bold">📱 pushed {new Date(a.push_sent_at).toLocaleString()}</span></>
                    )}
                  </div>
                </div>
                {/* QR toggle */}
                <button
                  onClick={() => loadQr(a)}
                  className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200"
                  title="Show QR"
                  data-testid={`ann-qr-btn-${a.id}`}
                >
                  <QrIcon className="w-4 h-4" />
                </button>
              </div>

              {qrCache[a.id] && (
                <div className="mt-3 flex items-center gap-3">
                  <div
                    className="bg-white rounded-lg p-2 w-24 h-24 flex items-center justify-center"
                    data-testid={`ann-qr-${a.id}`}
                    dangerouslySetInnerHTML={{ __html: qrCache[a.id] }}
                  />
                  <div className="text-xs text-slate-400 flex-1">
                    Print this on your booth signage. Visitors scan → auto-open redeem page with coupon code <span className="font-mono text-emerald-300">{a.coupon_code}</span>.
                  </div>
                </div>
              )}

              <div className="mt-2 flex gap-2">
                <button onClick={() => toggleActive(a)} className="px-2 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-white text-[11px] font-bold" data-testid={`ann-toggle-${a.id}`}>
                  {a.is_active ? 'Pause' : 'Resume'}
                </button>
                <button onClick={() => remove(a.id)} className="px-2 py-1 rounded-md bg-red-500/80 hover:bg-red-600 text-white text-[11px] font-bold flex items-center gap-1" data-testid={`ann-delete-${a.id}`}>
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {boothNumber && (
        <div className="mt-4 text-slate-500 text-[10px]">
          Announcements are attached to booth <span className="font-mono text-slate-300">{boothNumber}</span>.
        </div>
      )}
    </div>
  );
}

const inputCls = 'w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 rounded-lg px-3 py-2 text-white text-sm outline-none';

function toMysql(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
